import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronRight, Search, Filter, RefreshCw, Package, Calendar, User, MapPin, CheckSquare, Square, Minus, Zap, Play, X, AlertCircle, CheckCircle } from 'lucide-react';
import { ordersService, OrderWithLinesDto } from '../../../../services/ordersService';
import ruleGroupEngineService, { SystemRuleGroup, RuleGroupParameters, RuleGroupResult } from '../../../../services/ruleGroupEngineService';
import { useAuth } from '../../../../contexts/AuthContext';
import { BackButton } from '../../../common/BackButton';

interface NewOrdersProps {
  onBack?: () => void;
}

const NewOrders: React.FC<NewOrdersProps> = ({ onBack }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<OrderWithLinesDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLines, setSelectedLines] = useState<Set<string>>(new Set()); // key: "orderNo-lineNo"
  const [columnSearches, setColumnSearches] = useState<{[key: string]: string}>({
    orderNo: '',
    orderNoERP: '',
    customerNo: '',
    customerName: '',
    deliveryDate: '',
    warehouse: '',
    orderTotal: '',
    itemNo: '',
    description: ''
  });
  
  // Rule execution state
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [ruleGroups, setRuleGroups] = useState<SystemRuleGroup[]>([]);
  const [selectedRuleGroupId, setSelectedRuleGroupId] = useState<number>(0);
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [executingRule, setExecutingRule] = useState(false);
  const [ruleResult, setRuleResult] = useState<RuleGroupResult | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);

  useEffect(() => {
    loadOrders();
    loadRuleGroups();
  }, []);

  const loadRuleGroups = async () => {
    if (!user?.companyNo) return;
    
    try {
      const groups = await ruleGroupEngineService.getRuleGroups(user.companyNo);
      setRuleGroups(groups);
    } catch (err) {
      console.error('Error loading rule groups:', err);
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ordersService.getOpenOrdersWithLines();
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved lasting av ordre');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleOrderExpansion = (orderNo: number) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderNo)) {
      newExpanded.delete(orderNo);
    } else {
      newExpanded.add(orderNo);
    }
    setExpandedOrders(newExpanded);
  };

  const handleColumnSearchChange = (column: string, value: string) => {
    setColumnSearches(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const toggleLineSelection = (orderNo: number, lineNo: number) => {
    const key = `${orderNo}-${lineNo}`;
    const newSelected = new Set(selectedLines);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedLines(newSelected);
  };

  const toggleOrderSelection = (orderNo: number, lines: any[]) => {
    const orderKeys = lines.map(line => `${orderNo}-${line.lineNo}`);
    const allSelected = orderKeys.every(key => selectedLines.has(key));
    
    const newSelected = new Set(selectedLines);
    if (allSelected) {
      // Deselect all lines in this order
      orderKeys.forEach(key => newSelected.delete(key));
    } else {
      // Select all lines in this order
      orderKeys.forEach(key => newSelected.add(key));
    }
    setSelectedLines(newSelected);
  };

  // Calculate master checkbox state
  const getMasterCheckboxState = () => {
    if (filteredOrders.length === 0) return 'none';
    
    const allVisibleLineKeys: string[] = [];
    filteredOrders.forEach(orderWithLines => {
      orderWithLines.lines.forEach(line => {
        allVisibleLineKeys.push(`${orderWithLines.header.orderNo}-${line.lineNo}`);
      });
    });
    
    if (allVisibleLineKeys.length === 0) return 'none';
    
    const selectedVisibleCount = allVisibleLineKeys.filter(key => selectedLines.has(key)).length;
    
    if (selectedVisibleCount === 0) return 'none';
    if (selectedVisibleCount === allVisibleLineKeys.length) return 'all';
    return 'partial';
  };

  const toggleMasterCheckbox = () => {
    const masterState = getMasterCheckboxState();
    
    if (masterState === 'all') {
      // Deselect all visible lines
      deselectAllLines();
    } else {
      // Select all visible lines
      selectAllVisibleLines();
    }
  };

  const selectAllVisibleLines = () => {
    const newSelected = new Set(selectedLines);
    filteredOrders.forEach(orderWithLines => {
      orderWithLines.lines.forEach(line => {
        const key = `${orderWithLines.header.orderNo}-${line.lineNo}`;
        newSelected.add(key);
      });
    });
    setSelectedLines(newSelected);
  };

  const deselectAllLines = () => {
    setSelectedLines(new Set());
  };

  // Get unique order numbers from selected lines
  const getSelectedOrderNumbers = (): number[] => {
    const orderNumbers = new Set<number>();
    selectedLines.forEach(key => {
      const [orderNo] = key.split('-').map(Number);
      orderNumbers.add(orderNo);
    });
    return Array.from(orderNumbers).sort((a, b) => a - b);
  };

  // Open rule execution modal
  const openRuleModal = () => {
    const selectedOrders = getSelectedOrderNumbers();
    if (selectedOrders.length === 0) {
      alert('Velg minst én ordre eller ordrelinje før du kjører regler');
      return;
    }
    setShowRuleModal(true);
    setRuleResult(null);
  };

  // Execute rule group for selected orders
  const executeRuleForSelectedOrders = async () => {
    if (!user?.companyNo) return;
    
    if (selectedRuleGroupId === 0) {
      alert('Velg en regelgruppe');
      return;
    }

    const selectedOrders = getSelectedOrderNumbers();
    if (selectedOrders.length === 0) {
      alert('Ingen ordre valgt');
      return;
    }

    setExecutingRule(true);
    setRuleResult(null);

    try {
      // Build SQL filter for selected orders: Orders.OrderNo IN (1,2,3)
      const orderNoList = selectedOrders.join(',');
      const sqlFilter = `Orders.OrderNo IN (${orderNoList})`;

      // Create parameters with SQL filter
      const parameters: RuleGroupParameters = {
        ruleGroupID: selectedRuleGroupId,
        companyNo: user.companyNo,
        warehouseID: warehouseId,
        sessionID: `SESSION_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        debugMode: true,
        sqlExtraFilter: sqlFilter  // Send filter to correct property
      };

      // Execute rule group
      const result = await ruleGroupEngineService.executeRuleGroup(user.companyNo, parameters);
      
      setRuleResult(result);
      setShowResultModal(true);
      setShowRuleModal(false);

      // Reload orders to see changes
      if (result.success) {
        setTimeout(() => {
          loadOrders();
        }, 1000);
      }

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Feil ved kjøring av regel';
      alert(`Feil: ${errorMsg}`);
      console.error('Error executing rule:', err);
    } finally {
      setExecutingRule(false);
    }
  };

  const formatExecutionTime = (timeSpan: string): string => {
    return ruleGroupEngineService.formatExecutionTime(timeSpan);
  };

  // Function to check if an order should be shown based on search criteria
  const orderMatchesSearch = (orderWithLines: OrderWithLinesDto): boolean => {
    const order = orderWithLines.header;
    
    // Global search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesOrder = 
        order.orderNo.toString().includes(searchLower) ||
        order.orderNoERP.toLowerCase().includes(searchLower) ||
        order.customerNo.toLowerCase().includes(searchLower) ||
        (order.customerName || '').toLowerCase().includes(searchLower) ||
        (order.warehouse || '').toLowerCase().includes(searchLower);
      
      const matchesLines = orderWithLines.lines.some(line => 
        line.itemNo.toLowerCase().includes(searchLower) ||
        (line.description1 || '').toLowerCase().includes(searchLower)
      );
      
      if (!matchesOrder && !matchesLines) return false;
    }

    // Column searches for order fields
    if (columnSearches.orderNo && !order.orderNo.toString().includes(columnSearches.orderNo)) return false;
    if (columnSearches.orderNoERP && !order.orderNoERP.toLowerCase().includes(columnSearches.orderNoERP.toLowerCase())) return false;
    if (columnSearches.customerNo && !order.customerNo.toLowerCase().includes(columnSearches.customerNo.toLowerCase())) return false;
    if (columnSearches.customerName && !(order.customerName || '').toLowerCase().includes(columnSearches.customerName.toLowerCase())) return false;
    if (columnSearches.deliveryDate && !(order.deliveryDate || '').includes(columnSearches.deliveryDate)) return false;
    if (columnSearches.warehouse && !(order.warehouse || '').toLowerCase().includes(columnSearches.warehouse.toLowerCase())) return false;
    if (columnSearches.orderTotal && order.orderTotal && !order.orderTotal.toString().includes(columnSearches.orderTotal)) return false;

    // Column searches for order lines
    if (columnSearches.itemNo || columnSearches.description) {
      const matchingLines = orderWithLines.lines.some(line => {
        const itemMatch = !columnSearches.itemNo || line.itemNo.toLowerCase().includes(columnSearches.itemNo.toLowerCase());
        const descMatch = !columnSearches.description || (line.description1 || '').toLowerCase().includes(columnSearches.description.toLowerCase());
        return itemMatch && descMatch;
      });
      if (!matchingLines) return false;
    }

    return true;
  };

  const filteredOrders = useMemo(() => {
    return orders.filter(orderMatchesSearch);
  }, [orders, searchTerm, columnSearches]);

  // Calculate selection states for each order
  const getOrderSelectionState = (orderNo: number, lines: any[]) => {
    const orderKeys = lines.map(line => `${orderNo}-${line.lineNo}`);
    const selectedCount = orderKeys.filter(key => selectedLines.has(key)).length;
    
    if (selectedCount === 0) return 'none';
    if (selectedCount === orderKeys.length) return 'all';
    return 'partial';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>Laster ordre...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-medium">Feil ved lasting av ordre</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadOrders}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>Prøv igjen</span>
        </button>
      </div>
    );
  }

  const totalSelectedLines = selectedLines.size;

  return (
    <div className="w-full">
      <div className="mb-6 px-6 pt-6">
        <div className="flex items-center space-x-4 mb-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <Package size={24} />
            <span>Nye Ordre</span>
          </h1>
        </div>
        <p className="text-gray-600 mt-1">
          Oversikt over nye ordre med status "Ubehandlet" - {filteredOrders.length} av {orders.length} ordre vises
          {totalSelectedLines > 0 && (
            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {totalSelectedLines} linjer valgt
            </span>
          )}
        </p>
      </div>

      {/* Search and Controls */}
      <div className="bg-white rounded-lg shadow-sm border mb-4 p-4 mx-6">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Søk i alle felt inkludert varenummer og beskrivelse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex space-x-2">
            {totalSelectedLines > 0 && (
              <>
                <button
                  onClick={openRuleModal}
                  className="bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 text-sm flex items-center space-x-1"
                  title="Kjør regel for valgte ordre"
                >
                  <Zap size={14} />
                  <span>Kjør regel ({getSelectedOrderNumbers().length} ordre)</span>
                </button>
                <button
                  onClick={selectAllVisibleLines}
                  className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center space-x-1"
                >
                  <CheckSquare size={14} />
                  <span>Velg alle synlige</span>
                </button>
                <button
                  onClick={deselectAllLines}
                  className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 text-sm flex items-center space-x-1"
                >
                  <Square size={14} />
                  <span>Fjern valg</span>
                </button>
              </>
            )}
            <button
              onClick={loadOrders}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              <span>Oppdater</span>
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden mx-6 mb-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  <div className="flex items-center space-x-1">
                    <span>#</span>
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  <button
                    onClick={toggleMasterCheckbox}
                    className="flex items-center hover:text-blue-600"
                    title="Velg/fravelg alle synlige linjer"
                  >
                    {(() => {
                      const masterState = getMasterCheckboxState();
                      if (masterState === 'all') return <CheckSquare size={16} className="text-blue-600" />;
                      if (masterState === 'partial') return <Minus size={16} className="text-blue-600 bg-blue-100 rounded" />;
                      return <Square size={16} className="text-gray-400" />;
                    })()}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Ordrenr</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.orderNo}
                      onChange={(e) => handleColumnSearchChange('orderNo', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>ERP Ordrenr</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.orderNoERP}
                      onChange={(e) => handleColumnSearchChange('orderNoERP', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Kundenr</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.customerNo}
                      onChange={(e) => handleColumnSearchChange('customerNo', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Kundenavn</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.customerName}
                      onChange={(e) => handleColumnSearchChange('customerName', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Leveringsdato</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.deliveryDate}
                      onChange={(e) => handleColumnSearchChange('deliveryDate', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Lager</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.warehouse}
                      onChange={(e) => handleColumnSearchChange('warehouse', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Varenr</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.itemNo}
                      onChange={(e) => handleColumnSearchChange('itemNo', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Beskrivelse</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.description}
                      onChange={(e) => handleColumnSearchChange('description', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Total</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.orderTotal}
                      onChange={(e) => handleColumnSearchChange('orderTotal', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Linjer
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((orderWithLines) => {
                const order = orderWithLines.header;
                const isExpanded = expandedOrders.has(order.orderNo);
                const selectionState = getOrderSelectionState(order.orderNo, orderWithLines.lines);
                
                return (
                  <React.Fragment key={order.orderNo}>
                    <tr className="hover:bg-gray-50">
                      <td 
                        className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                        onClick={() => toggleOrderExpansion(order.orderNo)}
                      >
                        <div className="flex items-center">
                          {isExpanded ? (
                            <ChevronDown size={16} className="text-gray-400" />
                          ) : (
                            <ChevronRight size={16} className="text-gray-400" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button
                          onClick={() => toggleOrderSelection(order.orderNo, orderWithLines.lines)}
                          className="flex items-center hover:text-blue-600"
                        >
                          {selectionState === 'all' && <CheckSquare size={18} className="text-blue-600" />}
                          {selectionState === 'none' && <Square size={18} className="text-gray-400" />}
                          {selectionState === 'partial' && <Minus size={18} className="text-blue-600 bg-blue-100 rounded" />}
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{order.orderNo}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{order.orderNoERP}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{order.customerNo}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{order.customerName || '-'}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center space-x-1">
                          <Calendar size={14} className="text-gray-400" />
                          <span>{ordersService.formatDate(order.deliveryDate)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center space-x-1">
                          <MapPin size={14} className="text-gray-400" />
                          <span>{order.warehouse || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {orderWithLines.lines.length > 0 ? orderWithLines.lines[0].itemNo : '-'}
                          {orderWithLines.lines.length > 1 && <span className="text-gray-500 ml-1">+{orderWithLines.lines.length - 1}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {orderWithLines.lines.length > 0 ? (orderWithLines.lines[0].description1 || '-') : '-'}
                          {orderWithLines.lines.length > 1 && <span className="text-gray-500 ml-1">...</span>}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {ordersService.formatCurrency(order.orderTotal)}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {order.statusText}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {orderWithLines.lines.length} linjer
                        </div>
                      </td>
                    </tr>
                    
                    {isExpanded && orderWithLines.lines.length > 0 && (
                      <tr>
                        <td colSpan={13} className="px-4 py-2 bg-gray-50">
                          <div className="overflow-x-auto">
                            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
                              <Package size={16} />
                              <span>Ordrelinjer for ordre {order.orderNo}</span>
                            </h4>
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase w-12">
                                    <CheckSquare size={14} className="text-gray-400" />
                                  </th>
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Linjenr</th>
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Varenr</th>
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Beskrivelse</th>
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Antall</th>
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Enhet</th>
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Pris</th>
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Lager</th>
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Lokasjon</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {orderWithLines.lines.map((line) => {
                                  const lineKey = `${order.orderNo}-${line.lineNo}`;
                                  const isLineSelected = selectedLines.has(lineKey);
                                  
                                  return (
                                    <tr key={line.id} className="hover:bg-gray-100">
                                      <td className="py-2 px-3">
                                        <button
                                          onClick={() => toggleLineSelection(order.orderNo, line.lineNo)}
                                          className="flex items-center hover:text-blue-600"
                                        >
                                          {isLineSelected ? (
                                            <CheckSquare size={16} className="text-blue-600" />
                                          ) : (
                                            <Square size={16} className="text-gray-400" />
                                          )}
                                        </button>
                                      </td>
                                      <td className="py-2 px-3 text-gray-900">{line.lineNo}</td>
                                      <td className="py-2 px-3 text-gray-900 font-mono">{line.itemNo}</td>
                                      <td className="py-2 px-3 text-gray-900">{line.description1 || '-'}</td>
                                      <td className="py-2 px-3 text-gray-900 font-medium">
                                        {ordersService.formatQuantity(line.quantity)}
                                      </td>
                                      <td className="py-2 px-3 text-gray-900">{line.itemUnitCode || '-'}</td>
                                      <td className="py-2 px-3 text-gray-900">
                                        {ordersService.formatCurrency(line.price)}
                                      </td>
                                      <td className="py-2 px-3">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                          {ordersService.getOrderLineStatusText(line.lineStatus)}
                                        </span>
                                      </td>
                                      <td className="py-2 px-3 text-gray-900">{line.warehouse || '-'}</td>
                                      <td className="py-2 px-3 text-gray-900">{line.stdLocationName || '-'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <Package size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Ingen ordre funnet</h3>
            <p className="text-gray-500">
              {orders.length === 0
                ? 'Ingen ordre med status "Ubehandlet" funnet.'
                : 'Ingen ordre matcher søkekriteriene.'
              }
            </p>
            {(searchTerm || Object.values(columnSearches).some(search => search !== '')) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setColumnSearches({
                    orderNo: '',
                    orderNoERP: '',
                    customerNo: '',
                    customerName: '',
                    deliveryDate: '',
                    warehouse: '',
                    orderTotal: '',
                    itemNo: '',
                    description: ''
                  });
                }}
                className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Fjern alle filtre
              </button>
            )}
          </div>
        )}
      </div>

      {/* Rule Execution Modal */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center space-x-2">
                <Zap className="text-purple-600" size={24} />
                <span>Kjør Regelgruppe</span>
              </h2>
              <button
                onClick={() => setShowRuleModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Selected Orders Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">Valgte ordre</h3>
                <div className="text-sm text-blue-800">
                  {getSelectedOrderNumbers().length} ordre valgt: {getSelectedOrderNumbers().join(', ')}
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  SQL Filter: Orders.OrderNo IN ({getSelectedOrderNumbers().join(',')})
                </div>
              </div>

              {/* Rule Group Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Regelgruppe *
                </label>
                <select
                  value={selectedRuleGroupId}
                  onChange={(e) => setSelectedRuleGroupId(parseInt(e.target.value))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  disabled={executingRule}
                >
                  <option value={0}>Velg regelgruppe...</option>
                  {ruleGroups.map((rg) => (
                    <option key={rg.ruleGroupID} value={rg.ruleGroupID}>
                      {rg.ruleGroupID} - {rg.ruleGroupName} {rg.activeYesNo ? '(Aktiv)' : '(Inaktiv)'}
                    </option>
                  ))}
                </select>
                {selectedRuleGroupId > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    {ruleGroups.find(rg => rg.ruleGroupID === selectedRuleGroupId)?.description}
                  </div>
                )}
              </div>

              {/* Warehouse ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lager ID (valgfritt)
                </label>
                <input
                  type="text"
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  placeholder="La stå tom for standard"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  disabled={executingRule}
                />
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Viktig informasjon</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Regelen vil kun kjøres for de valgte ordrene</li>
                      <li>Endringer vil bli gjort i databasen</li>
                      <li>Denne operasjonen kan ikke angres automatisk</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowRuleModal(false)}
                disabled={executingRule}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                onClick={executeRuleForSelectedOrders}
                disabled={executingRule || selectedRuleGroupId === 0}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {executingRule ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    <span>Kjører...</span>
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    <span>Kjør Regelgruppe</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Modal */}
      {showResultModal && ruleResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center space-x-2">
                {ruleResult.success ? (
                  <CheckCircle className="text-green-600" size={24} />
                ) : (
                  <AlertCircle className="text-red-600" size={24} />
                )}
                <span>Regelkjøring Resultat</span>
              </h2>
              <button
                onClick={() => setShowResultModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Summary */}
              <div className={`border rounded-lg p-4 ${
                ruleResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`font-medium text-lg ${
                    ruleResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {ruleResult.success ? 'Suksess' : 'Feil'}
                  </span>
                  <span className="text-sm text-gray-600">
                    {formatExecutionTime(ruleResult.totalExecutionTime)}
                  </span>
                </div>
                <p className={`text-sm ${
                  ruleResult.success ? 'text-green-700' : 'text-red-700'
                }`}>
                  {ruleResult.message}
                </p>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-sm text-blue-600 mb-1">Regler utført</div>
                  <div className="text-2xl font-bold text-blue-900">{ruleResult.totalRulesExecuted}</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-sm text-green-600 mb-1">Suksess</div>
                  <div className="text-2xl font-bold text-green-900">{ruleResult.totalRulesSucceeded}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-sm text-red-600 mb-1">Feil</div>
                  <div className="text-2xl font-bold text-red-900">{ruleResult.totalRulesFailed}</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="text-sm text-purple-600 mb-1">Poster påvirket</div>
                  <div className="text-2xl font-bold text-purple-900">{ruleResult.totalRecordsAffected}</div>
                </div>
              </div>

              {/* Errors */}
              {ruleResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-medium text-red-900 mb-2">Feil</h3>
                  <div className="space-y-1">
                    {ruleResult.errors.map((error, idx) => (
                      <div key={idx} className="text-sm text-red-700">• {error}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {ruleResult.warnings.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-medium text-yellow-900 mb-2">Advarsler</h3>
                  <div className="space-y-1">
                    {ruleResult.warnings.map((warning, idx) => (
                      <div key={idx} className="text-sm text-yellow-700">• {warning}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Rule Results */}
              {ruleResult.ruleResults.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Detaljerte regelresultater</h3>
                  <div className="space-y-2">
                    {ruleResult.ruleResults.map((ruleResult, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {ruleResult.success ? (
                              <CheckCircle size={16} className="text-green-600" />
                            ) : (
                              <AlertCircle size={16} className="text-red-600" />
                            )}
                            <span className="font-medium text-sm">
                              Regel {ruleResult.ruleID}: {ruleResult.ruleName}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatExecutionTime(ruleResult.executionTime)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700 mb-1">{ruleResult.message}</div>
                        {ruleResult.recordsAffected > 0 && (
                          <div className="text-xs text-gray-500">
                            Poster påvirket: {ruleResult.recordsAffected}
                          </div>
                        )}
                        {ruleResult.errors.length > 0 && (
                          <div className="mt-2 text-xs text-red-600">
                            {ruleResult.errors.map((err, errIdx) => (
                              <div key={errIdx}>• {err}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowResultModal(false)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewOrders;
